import { generateDefaultValueForAttribute, validateAndRepairAttributeValue } from './attribute';
import { allSpecs } from './specs';
import type {
	ValidatorSpec,
	ValidatorSpecAttributesGroup,
	ValidatorSpecMark,
	ValidatorSpecNode,
} from './specs/types';
import type { AdfAttrs, AdfMark, PartialMark } from './types';

export type MarkValidationResult = {
	error?: string;
	isValid: boolean;
	repairedMark?: AdfMark;
};

export type NodeMarkValidationResult = {
	errors?: string[];
	repairedMarks?: AdfMark[];
};

/**
 * Get allowed marks for a given node type
 * Handles node variations and fallbacks
 */

export function getAllowedMarksForNode(nodeType: string): string[] {
	const allowedMarks = new Set<string>();

	// First try to get the main node rule
	const mainNodeRule = allSpecs[nodeType];

	const additionalRules: ValidatorSpec[] = [];
	const specKeys = Object.keys(allSpecs);
	specKeys.forEach((specKey) => {
		if (
			specKey.startsWith(nodeType) &&
			Array.isArray(allSpecs[specKey]) &&
			allSpecs[specKey][0] === nodeType
		) {
			additionalRules.push(allSpecs[specKey]);
		}
	});

	// Extract allowed marks from the node rule
	function extractMarksFromRule(rule: ValidatorSpec): void {
		if (!rule) {
			return;
		}

		if ('props' in rule && 'marks' in rule.props && rule.props.marks) {
			extractMarksFromItems(rule.props.marks.items);
		} else if (Array.isArray(rule) && rule.length >= 2) {
			// Handle array format [nodeType, { props: { marks: ... } }]
			const additionalSpec = rule[1] as ValidatorSpecNode;
			if (additionalSpec?.props?.marks?.items) {
				extractMarksFromItems(additionalSpec.props.marks.items);
			}
		}
	}

	function extractMarksFromItems(items: string[] | string[][]): void {
		if (Array.isArray(items)) {
			items.forEach((item) => {
				if (typeof item === 'string') {
					allowedMarks.add(item);
				} else if (Array.isArray(item)) {
					item.forEach((subItem) => {
						if (typeof subItem === 'string') {
							allowedMarks.add(subItem);
						}
					});
				}
			});
		}
	}
	extractMarksFromRule(mainNodeRule);
	additionalRules.forEach((rule) => extractMarksFromRule(rule));

	return Array.from(allowedMarks);
}

/**
 * Infer mark type from attrs if type is missing
 */
function inferMarkTypeFromAttrs(attrs?: AdfAttrs): string | undefined {
	if (!attrs || typeof attrs !== 'object') {
		return undefined;
	}

	// Check for specific attribute patterns that indicate mark types
	if (attrs.align) {
		return 'alignment';
	}
	if (attrs.annotationType) {
		return 'annotation';
	}

	if (attrs.size) {
		return 'border';
	}

	if (attrs.color) {
		return 'backgroundColor';
	}

	if (attrs.mode) {
		return 'breakout';
	}
	if (attrs.sources) {
		return 'dataConsumer';
	}
	if (attrs.localId && attrs.name !== undefined) {
		return 'fragment';
	}
	if (attrs.level) {
		return 'indentation';
	}
	if (attrs.href || attrs.title || attrs.collection || attrs.occurrenceKey) {
		return 'link';
	}
	if (attrs.type && (attrs.type === 'sub' || attrs.type === 'sup')) {
		return 'subsup';
	}
}

function inferMarkType(
	markType: string | undefined,
	allowedMarkTypes: string[],
	attrs?: AdfAttrs,
): string | undefined {
	// Step 1: Try to infer type from attrs
	if (!markType) {
		// Try to infer type from attrs
		markType = inferMarkTypeFromAttrs(attrs);
	}

	// Step 2: Try to infer, assuming type string is incomplete
	if (typeof markType === 'string' && !allowedMarkTypes.includes(markType)) {
		// Try partial matching - find marks that start with the given type
		const partialMatches = allowedMarkTypes.filter((allowedType) =>
			allowedType.startsWith(markType as string),
		);

		if (partialMatches.length > 0) {
			markType = partialMatches[0];
		}
	}

	return markType;
}

/**
 * Validate and repair a single mark's attributes
 */
function repairMarkAttrs(
	markType: string,
	attrs?: AdfAttrs,
): { error?: string; isValid: boolean; repairedAttrs?: AdfAttrs } {
	const markRule = allSpecs[markType] as ValidatorSpecMark;
	if (!markRule) {
		return { isValid: false, error: `Unknown mark type: ${markType}` };
	}

	const attrsRule = markRule.props.attrs as ValidatorSpecAttributesGroup;
	if (!attrsRule) {
		// Mark doesn't require attributes, not need to repair or validate attrs
		return { isValid: true };
	}

	// If no attrs provided, try to generate defaults for required attributes
	if (!attrs) {
		const defaultAttrs: AdfAttrs = {};
		const errors: string[] = [];

		for (const [attrName, attrRule] of Object.entries(attrsRule.props)) {
			if (!attrRule.optional) {
				const defaultValue = generateDefaultValueForAttribute(attrRule, attrName, markType);
				if (defaultValue === null) {
					errors.push(
						`Mark ${markType}: Required attribute '${attrName}' cannot have default value`,
					);
				} else {
					defaultAttrs[attrName] = defaultValue;
				}
			}
		}

		if (errors.length > 0) {
			return { isValid: false, error: errors.join('; ') };
		}

		return { isValid: true, repairedAttrs: defaultAttrs };
	}

	const repairedAttrs = { ...attrs };
	const errors: string[] = [];

	// Check all defined attributes in the rule
	for (const [attrName, attrRule] of Object.entries(attrsRule.props)) {
		const attrValue = attrs[attrName];

		// Validate and potentially repair attribute value
		const validation = validateAndRepairAttributeValue(attrName, attrValue, attrRule, markType);
		if (validation.isValid) {
			repairedAttrs[attrName] = validation.repairedValue;
		}
		// If attribute is not optional and validation failed, simply skip this attribute
		else if (!attrRule.optional) {
			errors.push(validation.error || `Invalid value for attribute '${attrName}'`);
		}
	}

	if (errors.length > 0) {
		return { isValid: false, error: errors.join('; ') };
	}

	return { isValid: true, repairedAttrs };
}

/**
 * Validate and repair a single mark
 */
function repairMark(mark: PartialMark, allowedMarkTypes: string[]): MarkValidationResult {
	if (!mark || typeof mark !== 'object') {
		return { isValid: false, error: 'Mark must be an object' };
	}

	let markType = mark.type;

	/**
	 * Step 1: If mark type is undefined or is not allowed,
	 * 	then it may be incomplete, so try to infer type from attrs
	 */
	if (!markType || !allowedMarkTypes.includes(markType)) {
		// Try to infer type from attrs
		markType = inferMarkType(markType, allowedMarkTypes, mark.attrs);
	}

	if (!markType) {
		return {
			isValid: false,
			error: `Mark type '${markType}' is invalid and could not be inferred`,
		};
	}

	// Step 2: Validate and repairmark attributes
	const attrValidationResult = repairMarkAttrs(markType, mark.attrs);
	if (!attrValidationResult.isValid) {
		return { isValid: false, error: attrValidationResult.error };
	}

	const repairedMark: AdfMark = {
		type: markType,
	};

	// Only add attrs if they exist and are not empty
	if (
		attrValidationResult.repairedAttrs &&
		Object.keys(attrValidationResult.repairedAttrs).length > 0
	) {
		repairedMark.attrs = attrValidationResult.repairedAttrs;
	}

	return { isValid: true, repairedMark };
}

/**
 * Main function repair marks for a given node type
 */
export function repairNodeMarks(nodeType: string, marks?: PartialMark[]): NodeMarkValidationResult {
	/**
	 * Currently makrs are always optional, so if marks are not provided,
	 * 	then simply consider it as valid.
	 */
	if (!marks || !Array.isArray(marks)) {
		return { repairedMarks: [] };
	}

	const allowedMarkTypes = getAllowedMarksForNode(nodeType);
	const repairedMarks: AdfMark[] = [];
	const errors: string[] = [];

	for (let i = 0; i < marks.length; i++) {
		const mark = marks[i];
		const result = repairMark(mark, allowedMarkTypes);

		if (result.isValid && result.repairedMark) {
			repairedMarks.push(result.repairedMark);
		} else {
			errors.push(result.error || `Can' repair mark ${mark.type} for node ${nodeType}`);
		}
	}

	return { repairedMarks, errors };
}
